'use client';
import { useAppSettings } from '@bells/core';
import { useAuth } from '@bells/core/auth';
import { useLanguage } from '@bells/core/i18n';
import { getInitials } from '@bells/core/lib/initals';
import { authVerifySignin, layoutAuthLogout } from '@bells/i18n';
import { baseLocale } from '@bells/i18n/runtime';
import { UserRole } from '@bells/schema/models/user';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@bells/ui/components/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@bells/ui/components/dropdown-menu';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@bells/ui/components/sidebar';
import { ChevronsUpDown, Gem, LogIn, LogOut } from 'lucide-react';
import Link from 'next/link';

export const NavUser = () => {
  const { isMobile } = useSidebar();
  const { isAuthed, currentUser, openSettings, setOpenSettings } = useAuth();
  const { language } = useLanguage();
  const { frontendUrl } = useAppSettings();

  const handleLogout = () => {
    window.location.replace(`${frontendUrl}/api/auth/logout`);
  };
  return isAuthed ? (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size='lg'
              className='!opacity-100 data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground'
            >
              <Avatar className='h-8 w-8 rounded-lg'>
                <AvatarFallback className='rounded-lg'>
                  {getInitials(currentUser?.name)}
                </AvatarFallback>
                {currentUser?.avatar && (
                  <AvatarImage src={currentUser?.avatar}></AvatarImage>
                )}
              </Avatar>
              <div className='grid flex-1 text-left text-sm leading-tight'>
                <span className='truncate font-semibold'>
                  {currentUser?.name}
                </span>
              </div>

              <ChevronsUpDown className='ml-auto size-4' />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className='w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg'
            side={isMobile ? 'bottom' : 'right'}
            align='end'
            sideOffset={4}
          >
            <DropdownMenuLabel className='p-0 font-normal'>
              <div className='flex items-center gap-2 px-1 py-1.5 text-left text-sm'>
                <Avatar className='h-8 w-8 rounded-lg'>
                  <AvatarFallback className='rounded-lg'>
                    {getInitials(currentUser?.name)}
                  </AvatarFallback>
                  {currentUser?.avatar && (
                    <AvatarImage src={currentUser?.avatar}></AvatarImage>
                  )}
                </Avatar>
                <div className='grid flex-1 text-left text-sm leading-tight'>
                  <span className='truncate font-semibold'>
                    {currentUser?.name}
                  </span>
                  {currentUser?.roles.includes(UserRole.VIP) && (
                    <div className='inline-flex items-center gap-1 truncate text-xs'>
                      <Gem className='h-4 w-4'></Gem>
                      <span>VIP Membership</span>
                    </div>
                  )}
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout}>
              <LogOut />
              {layoutAuthLogout()}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  ) : (
    <SidebarMenu className='lg:hidden'>
      <SidebarMenuItem>
        <SidebarMenuButton
          asChild
          className='flex items-center gap-2 rounded-lg bg-primary text-sm text-primary-foreground !opacity-100 transition-colors hover:bg-brand hover:text-brand-foreground'
        >
          <Link
            prefetch={false}
            href={
              language == baseLocale
                ? `/auth/signin/customer`
                : `/${language}/auth/signin/customer`
            }
          >
            <LogIn />
            <span>{authVerifySignin()}</span>
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    </SidebarMenu>
  );
};
